import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import { getWindowDimension } from "./analytics/deviceInformation";
import type { Accounting } from "./api/engage/engage.types";
import type { ShellUser } from "./api/types";
import { config } from "./config";

const MFE_OVERRIDES_SESSION_KEY = "_mfe";

// Seems like a lot of our APIs would require this config for the OPTION parameter:
// Access-Control-Allow-Headers: x-datadog-origin,x-datadog-parent-id,x-datadog-sampling-priority,x-datadog-trace-id
// https://docs.datadoghq.com/real_user_monitoring/connect_rum_and_traces/?tab=browserrum#how-are-rum-resources-linked-to-traces
const isSupportedLocation =
  "localhost" !== location.hostname.toLowerCase() &&
  !location.hostname.includes("labengage.sinch.com");

export const setExceptionMonitoringUserContext = (
  user: ShellUser,
  accounting: Partial<Accounting>
) => {
  const userObject = {
    id: String(user.id),
    language: user.language,
    roles: user.roles,
    projectId: user.activeProject,
    accountId: accounting.account_id,
  };
  datadogRum.setUser(userObject);
  datadogLogs.setUser(userObject);
};

export const setExceptionMonitoringProjectContext = (projectId: string) =>
  datadogRum.setGlobalContextProperty("selected_project", projectId);

const captureWindowDimensions = () =>
  datadogRum.setGlobalContextProperty("windowDimension", getWindowDimension());

// Docs: https://docs.datadoghq.com/real_user_monitoring/browser/
export const initExceptionMonitoring = ({
  missingCriticalBrowserSupport,
  missingBrowserVersionSupport,
}: {
  missingCriticalBrowserSupport: boolean;
  missingBrowserVersionSupport: boolean;
}) => {
  const { AUTH_CONFIG, DATADOG_APPLICATION_ID, DATADOG_CLIENT_TOKEN } = config;
  if (!DATADOG_APPLICATION_ID || !DATADOG_CLIENT_TOKEN || !AUTH_CONFIG.url) {
    console.warn("No exception monitoring, missing configuration.");
    return;
  }

  const isMmInstance = !!window.VENDOR_LABEL;

  const parsedAuthUrl = new URL(AUTH_CONFIG.url);

  const service = window.VENDOR_LABEL ?? "sinch-engage-web";

  const hostnameIsLocal = ["localhost", "127.0.0.1", "::1"].includes(
    location.hostname
  );
  const hasMFEOverrides =
    sessionStorage.getItem(MFE_OVERRIDES_SESSION_KEY) !== null;
  const env = hostnameIsLocal || hasMFEOverrides ? "dev" : "production";

  const sessionSampleRate = isMmInstance ? 50 : 100;

  datadogRum.init({
    applicationId: DATADOG_APPLICATION_ID,
    clientToken: DATADOG_CLIENT_TOKEN,
    site: isMmInstance ? "datadoghq.com" : "datadoghq.eu",
    service,
    env,
    version: COMMIT_HASH,
    sessionSampleRate,
    trackUserInteractions: true,
    startSessionReplayRecordingManually: true,
    beforeSend: (event, _context) => {
      // Add browser support context to all errors.
      if (event.type === "error") {
        event.context = {
          ...(event.context ?? {}),
          browserSupport: missingCriticalBrowserSupport
            ? "missing-critical-support"
            : missingBrowserVersionSupport
            ? "out-of-support"
            : "supported",
        };
      }
      return true;
    },
    allowedTracingUrls: [
      //  /localhost/,
      {
        match: /https:\/\/[a-z]+\.(messengerpeople|sinch)\.com/,
        propagatorTypes: ["datadog"],
      },
      {
        match: (origin: string) => {
          try {
            const parsedOrigin = new URL(origin);
            // No tracing headers for keycloak requests.
            if (
              parsedOrigin.hostname.toLowerCase() ===
              parsedAuthUrl.hostname.toLowerCase()
            ) {
              return false;
            }
            return (
              // Temporarily only add this more general hostname matching on staging
              // until we are sure every backend supports tracing.
              // After some time we can remove the `location.hostname` check.
              isSupportedLocation &&
              (parsedOrigin.hostname.endsWith(".messengerpeople.com") ||
                parsedOrigin.hostname.endsWith("engage.sinch.com"))
            );
          } catch {
            return false;
          }
        },
        propagatorTypes: ["datadog"],
      },
    ],
  });

  if (isMmInstance) {
    datadogLogs.init({
      clientToken: "puba146bcbc1604b980697ec8ae3ce9bbc7",
      datacenter: "us",
      forwardErrorsToLogs: true,
      sessionSampleRate,
      service,
      env,
    });
  }

  // Initial dimensions.
  captureWindowDimensions();
  // Capture new dimensions on resize.
  window.addEventListener("resize", captureWindowDimensions);
};

/**
 * Track an exception in in the exception tracking system, currently datadog.
 * @param error The error the caused the exception
 * @param context An optional context object to provide more information about the error
 */
export const trackException = (error: Error, context?: object | undefined) => {
  datadogRum.addError(error, context);
};
